import { useEffect, useState } from 'react';
import { MessageSquarePlus } from 'lucide-react';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/Button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/Dialog';
import { Textarea } from '@/components/ui/Textarea';
import { Label } from '@/components/ui/Label';
import { WorkPhotoCapture } from './WorkPhotoCapture';
import { useAddNote, uploadCapture } from '@/features/workMode/hooks';
import { compressImage } from '@/features/workMode/images';
import { describeWorkError } from '@/features/workMode/api';
import { NOTE_KINDS, type WorkInstructionStep, type WorkNoteKind, type WorkSession } from '@/features/workMode/types';

interface WorkNoteDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  session: WorkSession;
  /** Preselected kind (e.g. instruction_change from the instructions panel). */
  initialKind?: WorkNoteKind;
  /** Step the note is about, when it came from the instructions panel. */
  step?: WorkInstructionStep | null;
  steps?: WorkInstructionStep[];
}

const PLACEHOLDER: Record<WorkNoteKind, string> = {
  issue: 'e.g. The jig slipped twice this morning; the clamp is worn.',
  suggestion: 'e.g. If the screws came pre-sorted in a tray we would save a minute per unit.',
  tool_request: 'e.g. Need a 10 mm torque driver — using a borrowed one from Station 4.',
  instruction_change: 'e.g. Step 3 photo shows the old bracket. The new one mounts upside down.',
  safety: 'e.g. Oil on the floor by the press, nearly slipped.',
  kudos: 'e.g. Marco stayed late to clear my parts shortage. Legend.',
};

/** Observations, ideas, tool requests, instruction fixes, safety flags, shout-outs. */
export function WorkNoteDialog({ open, onOpenChange, session, initialKind = 'suggestion', step = null, steps = [] }: WorkNoteDialogProps) {
  const [kind, setKind] = useState<WorkNoteKind>(initialKind);
  const [body, setBody] = useState('');
  const [stepId, setStepId] = useState<string | null>(step?.id ?? null);
  const [photos, setPhotos] = useState<File[]>([]);
  const [busy, setBusy] = useState(false);
  const add = useAddNote();

  useEffect(() => {
    if (!open) return;
    setKind(initialKind);
    setBody('');
    setStepId(step?.id ?? null);
    setPhotos([]);
  }, [open, initialKind, step?.id]);

  const submit = async () => {
    if (!body.trim()) {
      toast.error('Write a few words first.');
      return;
    }
    setBusy(true);
    try {
      const paths: string[] = [];
      for (const f of photos) {
        const blob = await compressImage(f);
        paths.push(await uploadCapture(session.organization_id, session.id, blob, f.name.replace(/\.[^.]+$/, '') + '.jpg'));
      }
      await add.mutateAsync({ sessionId: session.id, kind, body: body.trim(), stepId: kind === 'instruction_change' ? stepId : null, photoPaths: paths });
      toast.success(kind === 'kudos' ? 'Shout-out sent.' : 'Thanks — a manager will see this.');
      onOpenChange(false);
    } catch (e) {
      toast.error(describeWorkError(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !busy && onOpenChange(o)}>
      <DialogContent className="max-h-[92vh] max-w-2xl overflow-y-auto rounded-2xl p-0">
        <DialogHeader className="border-b border-border px-6 py-4">
          <DialogTitle className="flex items-center gap-2 text-lg">
            <MessageSquarePlus className="h-5 w-5 text-info" />
            Leave a note
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-5 px-6 py-5">
          <div className="grid grid-cols-2 gap-2 sm:grid-cols-3">
            {NOTE_KINDS.map((k) => (
              <button
                key={k.value}
                type="button"
                onClick={() => setKind(k.value)}
                className={cn(
                  'flex min-h-[56px] items-center justify-center rounded-xl border px-3 py-2 text-center transition-colors',
                  kind === k.value
                    ? k.value === 'safety'
                      ? 'border-fresh-salmon-dark bg-fresh-salmon/25 text-fresh-salmon-dark'
                      : 'border-info bg-info-soft/30 text-info'
                    : 'border-border bg-card text-foreground hover:bg-secondary',
                )}
              >
                <span className="text-sm font-medium">{k.label}</span>
              </button>
            ))}
          </div>

          {kind === 'instruction_change' && steps.length > 0 && (
            <div className="space-y-2">
              <Label className="text-base">Which step?</Label>
              <div className="flex flex-wrap gap-2">
                {steps.map((s) => (
                  <button
                    key={s.id}
                    type="button"
                    onClick={() => setStepId((cur) => (cur === s.id ? null : s.id))}
                    className={cn(
                      'min-h-[44px] rounded-xl border px-3 py-2 text-sm',
                      stepId === s.id ? 'border-info bg-info-soft/30 text-info' : 'border-border bg-card hover:bg-secondary',
                    )}
                  >
                    <span className="font-medium">Step {s.step_number}</span>
                    <span className="ml-1.5 text-muted-foreground">{s.title}</span>
                  </button>
                ))}
              </div>
            </div>
          )}

          <div className="space-y-2">
            <Label className="text-base">Your note</Label>
            <Textarea value={body} onChange={(e) => setBody(e.target.value)} placeholder={PLACEHOLDER[kind]} className="min-h-[120px] text-base" autoFocus />
          </div>

          <div className="space-y-2">
            <Label className="text-base">Photos (optional)</Label>
            <WorkPhotoCapture files={photos} onChange={setPhotos} max={4} disabled={busy} />
          </div>
        </div>
        <div className="flex items-center justify-end gap-3 border-t border-border px-6 py-4">
          <Button variant="ghost" size="lg" onClick={() => onOpenChange(false)} disabled={busy}>
            Cancel
          </Button>
          <Button size="lg" onClick={submit} disabled={busy}>
            {busy ? 'Sending…' : 'Send note'}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
